/**
 * What the GM tells the player after an Attack Check, turned into the outcome
 * the damage rules need. Pure, so it is tested directly.
 *
 * The GM holds the target's Evade (owner decision, 2026-09-16), so the sheet
 * never knows whether an Attack landed or by how much. What it can know:
 *  - a natural 20 is a Critical Hit and a natural 1 a Critical Fail, whatever
 *    the Evade (RULE DCC-DEG-001, Rules for Survival p.15);
 *  - anything else hits only when the GM says so, and is an Amazing Success
 *    (+F damage, RULE DCC-DMG-007) only when the GM says so.
 */
import type { CheckResult } from '@dcc/rules/rules/checks.ts';
import type { AttackCheckOutcome } from '@dcc/rules/rules/attackResolution.ts';
import type { Degree } from '@dcc/rules/rules/degrees.ts';

export type GmCall = 'hit' | 'amazing';

export type DamageAllowance =
  | { kind: 'critical-fail' }
  | { kind: 'critical-hit' }
  | { kind: 'gm-decides' };

export function damageAllowanceFor(check: CheckResult): DamageAllowance {
  if (check.naturalOne) return { kind: 'critical-fail' };
  if (check.naturalTwenty) return { kind: 'critical-hit' };
  return { kind: 'gm-decides' };
}

/**
 * The Attack outcome to roll damage from. Returns null for a natural 1, which
 * cannot deal damage. A natural 20 is a Critical Hit whatever the GM call.
 */
export function damageOutcomeFor(check: CheckResult, call: GmCall): AttackCheckOutcome | null {
  const allowance = damageAllowanceFor(check);
  if (allowance.kind === 'critical-fail') return null;
  const degree: Degree = allowance.kind === 'critical-hit'
    ? 'critical-hit'
    : call === 'amazing' ? 'amazing-success' : 'standard-success';
  // The Evade itself is unknown to the sheet; only the degree reaches the damage rules.
  return { check, resolution: { degree, success: true, difficulty: check.total, total: check.total, margin: 0 } };
}
